/**
 * GMB Sync Service
 * @description Syncs GMB account data to database for task assignment
 * @author Senior Backend Developer
 */

import ApiService from './api';
import logger from '../utils/logger';

class GMBSyncService {
  /**
   * Sync a single GMB location to database
   * @param {number} userId - User ID
   * @param {object} location - GMB location data
   * @returns {Promise<object>} Synced account data
   */
  async syncLocation(userId, location) {
    try {
      if (!userId || !location) {
        throw new Error('User ID and location data are required');
      }

      // Extract location data
      const locationData = {
        user_id: userId,
        account_id: location.accountId || location.account_id || null,
        location_id: location.name || location.locationId || location.location_id || null,
        business_name: location.title || location.locationName || location.name || 'Unnamed Business',
        location_name: location.locationName || location.title || location.name || null,
        address: this._formatAddress(location.storefrontAddress || location.address),
        phone_number: location.phoneNumbers?.primaryPhone || location.phone || null,
        website: location.websiteUrl || location.website || null,
        category: location.categories?.primaryCategory?.displayName || 
                 location.primaryCategory?.displayName ||
                 location.category || null,
        location_data: location, // Store complete location data as JSON
        is_verified: location.metadata?.mapsUrl ? true : false
      };

      logger.log(`🔄 Syncing GMB location: ${locationData.business_name}`);
      logger.log('📦 Location data to sync:', {
        user_id: locationData.user_id,
        location_id: locationData.location_id,
        business_name: locationData.business_name,
        address: locationData.address
      });

      // Call backend API to sync
      logger.log('🌐 Calling API: POST /admin/gmb-account/sync');
      
      // Check authentication before making request
      const session = require('../utils/authUtils').getSession();
      const token = require('../utils/authUtils').getAutoToken();
      logger.log('🔐 Auth check:', {
        hasSession: !!session,
        hasToken: !!token,
        userId: session?.id,
        userRole: session?.role_id
      });
      
      const response = await ApiService.post('/admin/gmb-account/sync', locationData);
      
      logger.log('📥 Backend response:', response);
      logger.log(`✅ Synced: ${locationData.business_name}`);
      
      return response?.data?.account || response?.account;
    } catch (error) {
      logger.error('❌ Error syncing location:', error);
      logger.error('Error message:', error.message);
      logger.error('Error response:', error.response);
      logger.error('Failed location data:', location);
      throw error;
    }
  }

  /**
   * Sync multiple GMB locations to database
   * @param {number} userId - User ID
   * @param {Array} locations - Array of GMB location data
   * @returns {Promise<Array>} Array of synced accounts
   */
  async syncMultipleLocations(userId, locations) {
    try {
      if (!userId || !Array.isArray(locations)) {
        throw new Error('User ID and locations array are required');
      }

      logger.log(`Syncing ${locations.length} GMB locations for user ${userId}...`);

      const syncPromises = locations.map(location => 
        this.syncLocation(userId, location).catch(err => {
          logger.error(`Failed to sync location:`, err);
          return null; // Continue with other locations even if one fails
        })
      );

      const results = await Promise.all(syncPromises);
      const successCount = results.filter(r => r !== null).length;

      logger.log(`✅ Successfully synced ${successCount}/${locations.length} locations`);

      return results.filter(r => r !== null);
    } catch (error) {
      logger.error('Error syncing multiple locations:', error);
      throw error;
    }
  }

  /**
   * Sync GMB accounts from API response
   * @param {number} userId - User ID
   * @param {object} accountsData - GMB accounts API response
   * @returns {Promise<Array>} Array of synced accounts
   */
  async syncAccountsData(userId, accountsData) {
    try {
      // Extract locations from various possible response structures
      let locations = [];

      if (Array.isArray(accountsData)) {
        locations = accountsData;
      } else if (accountsData?.locations && Array.isArray(accountsData.locations)) {
        locations = accountsData.locations;
      } else if (accountsData?.data?.locations) {
        locations = accountsData.data.locations;
      }

      if (locations.length === 0) {
        logger.warn('No locations found to sync');
        return [];
      }

      return await this.syncMultipleLocations(userId, locations);
    } catch (error) {
      logger.error('Error syncing accounts data:', error);
      throw error;
    }
  }

  /**
   * Format address from GMB address object
   * @param {object} address - GMB address object
   * @returns {string} Formatted address string
   */
  _formatAddress(address) {
    if (!address) return null;

    const parts = [];

    if (address.addressLines && Array.isArray(address.addressLines)) {
      parts.push(...address.addressLines);
    }

    if (address.locality) parts.push(address.locality);
    if (address.administrativeArea) parts.push(address.administrativeArea);
    if (address.postalCode) parts.push(address.postalCode);
    if (address.regionCode) parts.push(address.regionCode);

    return parts.filter(Boolean).join(', ') || null;
  }

  /**
   * Get synced GMB accounts for a user from database
   * @param {number} userId - User ID
   * @returns {Promise<Array>} Array of GMB accounts
   */
  async getUserAccounts(userId) {
    try {
      const response = await ApiService.get(`/admin/gmb-account/user/${userId}`);
      return response?.data?.accounts || response?.accounts || [];
    } catch (error) {
      logger.error('Error fetching user accounts:', error);
      return [];
    }
  }
}

// Export singleton instance
const gmbSyncService = new GMBSyncService();
export default gmbSyncService;
